// apps/client/src/services/xeroSync.ts

import { apiClient } from './apiClient';
import {
  XeroSyncDispatchRequestBody,
  XeroSyncDispatchResponse,
  XeroSyncProgressResponse,
  XeroSyncHistoryResponse,
  XeroSyncHistoryRequestQuery,
} from '@avanzu/types';

/**
 * Dispatch a Xero sync job to the background queue
 * POST /organisations/:organisationId/xero/sync/dispatch
 */
export const dispatchXeroSync = async (
  organisationId: string,
  body: XeroSyncDispatchRequestBody
): Promise<XeroSyncDispatchResponse> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/xero/sync/dispatch`,
    body
  );
  const data: XeroSyncDispatchResponse = await response.json();
  return data;
};

/**
 * Get the progress of a specific sync job
 * GET /organisations/:organisationId/xero/sync/progress/:jobId
 */
export const getXeroSyncProgress = async (
  organisationId: string,
  jobId: string
): Promise<XeroSyncProgressResponse> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/xero/sync/progress/${jobId}`
  );
  const data: XeroSyncProgressResponse = await response.json();
  return data;
};

/**
 * Get sync job history for the organisation
 * GET /organisations/:organisationId/xero/sync/history
 */
export const getXeroSyncHistory = async (
  organisationId: string,
  params?: XeroSyncHistoryRequestQuery
): Promise<XeroSyncHistoryResponse> => {
  const queryParams = new URLSearchParams();

  if (params?.syncType) {
    queryParams.append('syncType', params.syncType);
  }
  if (params?.limit) {
    queryParams.append('limit', params.limit.toString());
  }

  const queryString = queryParams.toString();
  const url = `/organisations/${organisationId}/xero/sync/history${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get(url);
  const data: XeroSyncHistoryResponse = await response.json();
  return data;
};

/**
 * Get Xero connection status for the organisation
 * GET /organisations/:organisationId/xero/status
 * (Renamed from getXeroSyncStatus for clarity)
 */
export const getXeroConnectionStatus = async (
  organisationId: string
): Promise<{ success: boolean; connection: any; message?: string }> => {
  const response = await apiClient.get(`/organisations/${organisationId}/xero/status`);
  const data = await response.json();
  return data;
};

/**
 * Cancel a running sync job
 * DELETE /organisations/:organisationId/xero/sync/cancel/:jobId
 */
export const cancelXeroSync = async (
  organisationId: string,
  jobId: string
): Promise<{ success: boolean; message?: string }> => {
  const response = await apiClient.delete(
    `/organisations/${organisationId}/xero/sync/cancel/${jobId}`
  );
  const data = await response.json();
  return data;
};
